import { ChangeDetectionStrategy, Component, Signal, computed, effect, inject, output, signal } from '@angular/core';
import { CsIconModule } from '@components/icon';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { ConnectionService } from '@services/connection-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';

/** One slot of the review row: a photo the user uploaded, or an empty card inviting them to take it. */
export interface QuizPhotoReviewSlot {
  checkupId: string;
  imageUrl: string;
  label: string;
  type: string;
}

@Component({
  selector: 'app-quiz-photo-review',
  templateUrl: './quiz-photo-review.html',
  standalone: true,
  imports: [CsIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuizPhotoReviewComponent {
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly connectionService = inject(ConnectionService);
  private readonly commonUtilityHelper = inject(CommonUtilityHelper);

  /** Tapping an empty card opens capture for that checkup type. */
  readonly capture = output<string>();
  /** Tapping an uploaded photo opens its instant-checkup detail. */
  readonly openInstantCheckup = output<string>();

  readonly checkIcon: string = 'circle_tick_filled';
  readonly dashedBorderCardImage: string = '/assets/images/quiz-photo-dashed-border-card.svg';

  private readonly slotsState = signal<QuizPhotoReviewSlot[]>([]);
  readonly slots: Signal<QuizPhotoReviewSlot[]> = this.slotsState.asReadonly();
  readonly hasMissingPhoto: Signal<boolean> = computed(() => this.slots().some((slot) => !slot.imageUrl));

  private readonly checkupTypes: string[] = ['HAIR_TOP', 'HAIR_FRONT'];

  constructor() {
    effect((): void => {
      void this.resolveSlots();
    });
  }

  onSlotClick(slot: QuizPhotoReviewSlot): void {
    if (slot.imageUrl && slot.checkupId) {
      this.openInstantCheckup.emit(slot.checkupId);
      return;
    }
    this.capture.emit(slot.type);
  }

  onSlotKeydown(slot: QuizPhotoReviewSlot, event: Event): void {
    event.preventDefault();
    this.onSlotClick(slot);
  }

  private async resolveSlots(): Promise<void> {
    const slots = await Promise.all(this.checkupTypes.map(async (type) => {
      const checkup = await this.instantCheckupService.getRecentInstantCheckupOfType(
        this.connectionService.getActingUser(), type,
      );
      const imageUrl = await this.isDisplayableImage(checkup?.imagePath) ? checkup.imagePath : '';
      return {
        checkupId: imageUrl ? checkup?.objectId || '' : '',
        imageUrl,
        label: this.instantCheckupService.getPhotoTypeLabel(type),
        type,
      };
    }));
    this.slotsState.set(slots);
  }

  private async isDisplayableImage(url?: string): Promise<boolean> {
    if (!url) return false;
    return !(await this.commonUtilityHelper.checkImageDeleted(url));
  }
}
